import React, { useEffect, useState } from 'react';
import { Menu, X } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { Link, useLocation } from 'react-router-dom';
import { MegaMenu } from './MegaMenu';
const navLinks = [{
  label: 'Services',
  route: '/services',
  hasMegaMenu: true,
  megaMenuKey: 'services'
}, {
  label: 'Why AccuTemp',
  route: '/why-accutemp',
  hasMegaMenu: true,
  megaMenuKey: 'why-accutemp'
}, {
  label: 'Service areas',
  route: '/areas-we-cover',
  hasMegaMenu: true,
  megaMenuKey: 'service-areas'
}, {
  label: 'Case studies',
  route: '/case-studies',
  hasMegaMenu: true,
  megaMenuKey: 'case-studies'
}, {
  label: 'Resources',
  route: '/knowledge-hub',
  hasMegaMenu: true,
  megaMenuKey: 'resources'
}];
export function NavbarV2() {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeMegaMenu, setActiveMegaMenu] = useState<string | null>(null);
  const location = useLocation();
  useEffect(() => {
    const handleScroll = () => setScrolled(window.scrollY > 50);
    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);
  useEffect(() => {
    setMenuOpen(false);
    setActiveMegaMenu(null);
  }, [location.pathname]);
  useEffect(() => {
    document.body.style.overflow = menuOpen ? 'hidden' : '';
    return () => {
      document.body.style.overflow = '';
    };
  }, [menuOpen]);
  const isHome = location.pathname === '/';
  const textStyle = isHome && !scrolled ? 'text-white/90' : 'text-primary-dark/80';
  const hoverStyle = isHome && !scrolled ? 'hover:text-white' : 'hover:text-accent';
  const bgStyle = isHome && !scrolled ? 'bg-transparent' : 'bg-[#E8F0F8] shadow-lg';
  return <>
      <motion.nav initial={{
      y: -100
    }} animate={{
      y: 0
    }} transition={{
      duration: 0.5
    }} className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${bgStyle} ${isHome && !scrolled ? 'py-8' : 'py-4'}`} onMouseLeave={() => setActiveMegaMenu(null)} data-id="element-339">
        <div className="container mx-auto px-6 md:px-12 lg:px-16" data-id="element-340">
          <div className="grid grid-cols-3 items-center" data-id="element-341">
            <div className={`hidden lg:flex items-center justify-start gap-8 text-sm font-medium transition-colors ${textStyle}`} data-id="element-342">
              {navLinks.slice(0, 3).map(link => <div key={link.label} className="relative" onMouseEnter={() => link.hasMegaMenu && setActiveMegaMenu(link.megaMenuKey)} data-id="element-343">
                  <Link to={link.route} className={`transition-colors ${hoverStyle} ${location.pathname.startsWith(link.route) ? isHome && !scrolled ? 'text-white' : 'text-primary-dark' : ''}`} data-id="element-344">
                    {link.label}
                  </Link>
                </div>)}
            </div>

            <button onClick={() => setMenuOpen(!menuOpen)} className={`lg:hidden justify-self-start transition-colors ${isHome && !scrolled ? 'text-white hover:text-white/80' : menuOpen ? 'text-white hover:text-white/80' : 'text-primary-dark hover:text-primary-dark/70'}`} aria-label={menuOpen ? 'Close menu' : 'Open menu'} data-id="element-345">
              {menuOpen ? <X size={28} data-id="element-346" /> : <Menu size={28} data-id="element-347" />}
            </button>

            <Link to="/" className="justify-self-center" data-id="element-348">
              <img src={isHome && !scrolled ? 'https://cdn.magicpatterns.com/uploads/m22cd52VGFAbga9BNLUyHJ/accutemp-logo-white.svg' : 'https://cdn.magicpatterns.com/uploads/gniRXuhgWi11P326tXmzet/accutemp-logo-colour.svg'} alt="AccuTemp Air Conditioning" className="h-9 md:h-11 w-auto transition-all duration-300" data-id="element-349" />
            </Link>

            <div className="hidden lg:flex items-center justify-end gap-8" data-id="element-350">
              <div className={`flex items-center gap-8 text-sm font-medium transition-colors ${textStyle}`} data-id="element-351">
                {navLinks.slice(3).map(link => <div key={link.label} className="relative" onMouseEnter={() => link.hasMegaMenu && setActiveMegaMenu(link.megaMenuKey)} data-id="element-352">
                    <Link to={link.route} className={`transition-colors ${hoverStyle} ${location.pathname.startsWith(link.route) ? isHome && !scrolled ? 'text-white' : 'text-primary-dark' : ''}`} data-id="element-353">
                      {link.label}
                    </Link>
                  </div>)}
              </div>
              <Link to="/contact" className={`inline-flex text-sm font-medium px-5 py-2.5 rounded-full transition-all duration-300 ${isHome && !scrolled ? 'border border-white/30 hover:bg-white hover:text-primary-dark text-white' : 'bg-accent hover:bg-accent/90 text-white'}`} data-id="element-354">
                Contact us
              </Link>
            </div>
            <div className="lg:hidden justify-self-end w-8" data-id="element-355"></div>
          </div>
        </div>
        <AnimatePresence data-id="element-356">
          {activeMegaMenu && <MegaMenu isOpen={true} category={activeMegaMenu} data-id="element-357" />}
        </AnimatePresence>
      </motion.nav>

      <AnimatePresence data-id="element-358">
        {menuOpen && <>
            <motion.div initial={{
          opacity: 0
        }} animate={{
          opacity: 1
        }} exit={{
          opacity: 0
        }} transition={{
          duration: 0.3
        }} className="fixed inset-0 z-40 bg-black/50 backdrop-blur-sm lg:hidden" onClick={() => setMenuOpen(false)} data-id="element-359" />
            <motion.div initial={{
          x: '100%'
        }} animate={{
          x: 0
        }} exit={{
          x: '100%'
        }} transition={{
          type: 'spring',
          damping: 30,
          stiffness: 300
        }} className="fixed top-0 right-0 bottom-0 z-40 w-full max-w-sm bg-primary-dark shadow-2xl lg:hidden" data-id="element-360">
              <div className="flex flex-col h-full pt-24 pb-12 px-8" data-id="element-361">
                <nav className="flex flex-col gap-1 flex-1" data-id="element-362">
                  {navLinks.map((link, index) => <motion.div key={link.label} initial={{
                opacity: 0,
                x: 30
              }} animate={{
                opacity: 1,
                x: 0
              }} exit={{
                opacity: 0,
                x: 20
              }} transition={{
                duration: 0.3,
                delay: index * 0.06
              }} data-id="element-363">
                      <Link to={link.route} onClick={() => setMenuOpen(false)} className={`group flex items-center gap-3 text-2xl font-serif py-4 hover:text-accent transition-colors border-b border-white/5 ${location.pathname.startsWith(link.route) ? 'text-accent' : 'text-white'}`} data-id="element-364">
                        <span data-id="element-365">{link.label}</span>
                      </Link>
                    </motion.div>)}
                </nav>
                <motion.div initial={{
              opacity: 0
            }} animate={{
              opacity: 1
            }} transition={{
              duration: 0.4,
              delay: 0.3
            }} className="space-y-6" data-id="element-366">
                  <Link to="/contact" onClick={() => setMenuOpen(false)} className="block w-full bg-accent hover:bg-accent/90 text-white text-center px-6 py-4 rounded-full font-medium transition-colors" data-id="element-367">
                    Contact us
                  </Link>
                  <div className="flex items-center gap-4 text-white/40 text-sm" data-id="element-368">
                    <a href="https://linkedin.com/company/accutemp" target="_blank" rel="noopener noreferrer" className="hover:text-white transition-colors" data-id="element-369">
                      LinkedIn
                    </a>
                    <span className="w-1 h-1 rounded-full bg-white/30" data-id="element-370"></span>
                    <span data-id="element-371">Coventry, CV6 4DD</span>
                  </div>
                </motion.div>
              </div>
            </motion.div>
          </>}
      </AnimatePresence>
    </>;
}